import { Events, on } from '../core/events.js';
import { applyQuotaInfo } from './quota-state.js';
import { t, formatNumber } from '../../shared/i18n/index.js';

let cached = null, pending = null, generation = 0;
export function invalidateStorageInfo() { cached = null; }
function clearCache() { cached = null; pending = null; generation++; }
on(Events.AUTH_LOGOUT, clearCache);
on(Events.AUTH_LOGIN, clearCache);

/**
 * Storage Service
 *
 * Fetches storage quota information for the current user.
 */

/**
 * Get attachment quota, map/vault totals and sizes of accessible documents.
 * Old servers return null for unavailable map and vault measurements.
 */
export function getStorageInfo({ force = false } = {}) {
  if (pending) return pending;
  if (!force && cached && Date.now() - cached.at < 15000) return Promise.resolve(cached.value);
  const started = generation;
  const request = fetchStorageInfo().then(value => {
    if (started !== generation) throw new Error('Account changed while reading storage');
    applyQuotaInfo(value);
    cached = { at: Date.now(), value }; return value;
  }).finally(() => { if (pending === request) pending = null; });
  pending = request;
  return request;
}

async function fetchStorageInfo() {
  const response = await fetch('/api/account/storage', {
    credentials: 'include',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch storage info (${response.status})`);
  }
  const data = await response.json();
  return {
    storageUsed: data.storage_used,
    storageQuota: data.storage_quota,
    attachmentCount: data.blob_count,
    mapBytes: Number.isFinite(data.map_bytes) ? data.map_bytes : null,
    vaultBytes: Number.isFinite(data.vault_bytes) ? data.vault_bytes : null,
    attachmentBytes: Number.isFinite(data.attachment_bytes) ? data.attachment_bytes : data.storage_used,
    totalBytes: Number.isFinite(data.total_bytes) ? data.total_bytes : null,
    // Maps this user was removed from, kept (and counted) until they recover them.
    pendingRecoveryBytes: Number.isFinite(data.pending_recovery_bytes) ? data.pending_recovery_bytes : 0,
    pendingRecoveries: (data.pending_recoveries || []).map(item => ({
      docId: item.doc_id, bytes: item.bytes, expiresAt: item.expires_at || null,
    })),
    quotaScope: data.quota_scope || 'attachments',
    documents: new Map((data.documents || []).map(doc => [doc.doc_id, {
      mapBytes: doc.map_bytes, attachmentBytes: doc.attachment_bytes, attachmentCount: doc.attachment_count, role: doc.role,
      ownerOverQuota: doc.owner_over_quota === true,
    }])) ,
  };
}

/**
 * Format bytes to human-readable string.
 * @param {number} bytes
 * @returns {string}
 */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return t('vault.size.unavailableShort');
  if (bytes === 0) return sizeLabel(0, 'b', 0);
  const units = ['b', 'kb', 'mb', 'gb'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return sizeLabel(value, units[i], value < 10 ? 1 : 0);
}

/**
 * "1.5 MB" in the current language: its decimal separator and unit names.
 * Rounds like toFixed first, so English output is unchanged.
 * @param {number} value
 * @param {'b'|'kb'|'mb'|'gb'} unit
 * @param {number} digits - Fraction digits
 */
export function sizeLabel(value, unit, digits) {
  const rounded = Number(value.toFixed(digits));
  const size = formatNumber(rounded, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false });
  return t(`vault.size.${unit}`, { size });
}
